<app-legals-header title="Terms &amp; Conditions" />

<section
  class="legal-content legal-content--uppercase-titles section"
  aria-label="Terms &amp; Conditions content"
>
  <div class="legal-content__inner container subheading-dark">
    <p class="legal-content__intro">
      These Terms of Use only govern access to EVRN’s standard subscription plans as further
      described at evrn.world/plans. If you are interested in accessing EVRN on an enterprise-wide
      basis, please contact us at
      <a
        class="legal-content__link"
        href="mailto:enterprise@evrn.world"
        target="_blank"
        rel="noopener noreferrer"
        >enterprise&#64;evrn.world</a
      >
      for a copy of our enterprise terms and conditions.
    </p>

    <p>
      Please read these EVRN Terms of Service (these “Terms”) carefully. The EVRN application and
      related tools, platform, products, website, and the services and resources available via the
      EVRN application (collectively, the “Services”) are controlled by Cuebric, Inc. (“Cuebric”).
      These Terms are entered into by and between Cuebric and the entity or person placing an order
      for, registering for, or otherwise accessing the Services (“Customer”). The agreement between
      Cuebric and Customer consists of these Terms together with all fully executed ordering
      documents or online registration requests that are accepted by Cuebric (each, an “Order”) and
      any supplemental terms that may be presented to you in connection with specific products or
      features of the Services (collectively, this “Agreement”). This Agreement is effective as of
      the date of Customer’s initial access to the Services through any online provisioning,
      registration, or order process (the “Effective Date”). You acknowledge that if you are using
      the Services on behalf of, or within your capacity as, a representative, agent, or employee of
      any entity, then “Customer” as used herein will apply to such entity and such individual.
    </p>

    <p>
      BY CLICKING “I ACCEPT,” REGISTERING FOR OR OTHERWISE ACCESSING ANY PORTION OF THE SERVICES, OR
      EXECUTING AN ORDER, YOU REPRESENT AND WARRANT THAT: (1) YOU HAVE READ, UNDERSTAND, AND AGREE
      TO BE BOUND BY THIS AGREEMENT, (2) CUSTOMER IS NOT BARRED FROM USING THE SERVICES UNDER THE
      LAWS OF THE UNITED STATES, ITS PLACE OF RESIDENCE OR ANY OTHER APPLICABLE JURISDICTION, (3)
      YOU HAVE THE AUTHORITY TO ENTER INTO THIS AGREEMENT PERSONALLY OR, IF YOU ARE ACCESSING OR
      USING THE SERVICES ON BEHALF OF AN ENTITY, ON BEHALF OF CUSTOMER, AND (4) IF YOU ARE AN
      INDIVIDUAL, YOU ARE AT LEAST EIGHTEEN (18) YEARS OF AGE AND HAVE THE LEGAL CAPACITY TO ENTER
      INTO A BINDING AGREEMENT. IF CUSTOMER DOES NOT AGREE TO BE BOUND BY THE TERMS OF USE, OR IF
      YOU ARE UNDER EIGHTEEN (18) YEARS OF AGE, CUSTOMER MAY NOT ACCESS OR USE THE SERVICES. BY
      CLICKING “I ACCEPT,” REGISTERING FOR OR OTHERWISE ACCESSING ANY PORTION OF THE SERVICES, OR
      EXECUTING OR SUBMITTING AN ORDER, CUSTOMER IS ACCEPTING AND AGREEING TO BE BOUND BY ALL OF THE
      PROVISIONS OF THIS AGREEMENT.
    </p>

    <p>
      IF CUSTOMER SUBSCRIBES TO A RECURRING SUBSCRIPTION TO THE SERVICES FOR A TERM (AN “INITIAL
      SUBSCRIPTION TERM”), THEN, UNLESS OTHERWISE INDICATED IN THE ORDER, CUSTOMER’S SUBSCRIPTION
      WILL BE AUTOMATICALLY RENEWED FOR ADDITIONAL PERIODS OF THE SAME DURATION AS THE INITIAL
      SUBSCRIPTION TERM AT CUEBRIC’S THEN-CURRENT FEES FOR SUCH SUBSCRIPTION UNLESS CUSTOMER
      DECLINES TO RENEW THE SUBSCRIPTION IN ACCORDANCE WITH SECTION 3.3 (TERMINATION OF
      SUBSCRIPTIONS) BELOW.
    </p>

    <section class="legal-section" aria-labelledby="section-1">
      <h2 id="section-1" class="legal-section__title title-small-dark">1. Services</h2>
      <div class="legal-section__body">
        <div>
          <p class="legal-section__lead">1.1. <strong>Description of the Services</strong>.</p>
          <p>
            Cuebric provides a browser-based AI-powered platform that enables Customers to convert
            2D images into interactive 3D scenes (each, a “Scene”), including the ability to move
            assets, create and travel between cameras, adjust lighting, resize assets, render flat
            images, and export meshes.
          </p>
        </div>
        <div>
          <p class="legal-section__lead">1.2. <strong>Access and Use of Services</strong>.</p>
          <p>
            Subject to Customer’s ongoing compliance with this Agreement, Cuebric grants Customer a
            non-exclusive, non-transferable right, during the Term (as defined in Section 3.1
            (Term)), to access and use the Services identified in each active Order solely for
            Customer’s internal business purposes and solely in accordance with the published
            documentation for the Services (“Documentation”). Customer’s right to access the
            Services is subject to Customer’s payment of all applicable fees and is limited to the
            duration of the applicable Subscription Term. Customer’s usage of certain features of
            the Services is limited to the number of credits per month specified in the applicable
            Order (the “Monthly Credit Allowance”), along with any other usage caps or limitations
            set forth in the applicable Order. Customer is solely responsible for ensuring that its
            systems meet the hardware, software and any other applicable system requirements for the
            Services as specified in the Documentation.
          </p>
        </div>
        <div>
          <p class="legal-section__lead">1.3. <strong>Accounts</strong>.</p>
          <p>
            As part of the set-up process, Customer may be asked to create one or more accounts on
            the Services (each, an “Account”) and provide certain information as prompted in the
            account registration process. Customer represents and warrants that: (i) all required
            Account registration information submitted is truthful and accurate; and (ii) Customer
            will maintain the accuracy of such information. Customer is responsible for maintaining
            the confidentiality of all Account login information and is fully responsible for all
            activities that occur under Customer’s Accounts. Customer will use reasonable efforts to
            prevent any unauthorized access or use of the Services and Customer agrees to
            immediately notify Cuebric of any unauthorized use, or suspected unauthorized use, of
            the Accounts or any other breach of security. Additionally, if there is unauthorized
            access or use by anyone who obtained access directly or indirectly through Customer,
            Customer will also take all steps reasonably necessary to terminate the unauthorized
            access or use and cooperate and assist with any actions taken by Cuebric to remediate
            any issues resulting from, or related to, such unauthorized access or use. Cuebric will
            not be liable for any loss or damage arising from any unauthorized use of the Accounts
            or Customer’s failure to comply with the above requirements.
          </p>
        </div>
        <div>
          <p class="legal-section__lead">1.4. <strong>AI Functionality</strong>.</p>
          <p>
            Customer acknowledges that the Services leverage artificial intelligence (“AI”)
            technology, including by using Customer Data to generate, modify, and refine output
            (“Output”). The Services incorporate a combination of Cuebric’s proprietary AI models
            and third-party AI models (collectively, “Third-Party AI Providers”). Cuebric does not
            control the Third-Party AI Providers and makes no representation or warranty regarding
            their performance, availability, or data handling practices. Due to the nature of AI
            technology, Output may be unpredictable, and may include inaccurate or imperfect
            responses. Before using any Output, Customer is solely responsible for reviewing the
            Output for accuracy, safety, and compliance with applicable laws. Customer assumes all
            responsibility for the Output. All Output will be deemed to be Customer Data that, as
            between Customer and Cuebric, is owned by Customer; provided, however, that nothing in
            this Agreement will be deemed to restrict Cuebric or the Services from independently
            providing the same or similar Output to any other customer or user based on the same or
            similar input from such other customer or user. For clarity, the foregoing sentence
            applies solely to output that is independently generated by the Services in response to
            another customer’s or user’s input and does not authorize Cuebric to reproduce,
            disclose, or distribute any Customer Data to any other customer or user. Customer is and
            will remain fully responsible and liable for the deployment of the Services for
            Customer’s use case and Customer acknowledges that Cuebric does not control how Customer
            deploys or implements the Services. Customer will at all times use the Services in
            compliance with applicable laws.
          </p>
        </div>
        <div>
          <p class="legal-section__lead">1.5. <strong>Restrictions</strong>.</p>
          <p>
            To the maximum extent permitted by applicable law, Customer shall not, directly or
            indirectly, and shall not authorize any person to: (i) decompile, disassemble, reverse
            engineer or attempt to reconstruct or discover any source code, structure, ideas,
            algorithms, or other hidden or non-public elements of, (ii) translate, adapt, publish,
            reproduce, distribute or modify, (iii) except as authorized by Cuebric, write or develop
            any program based upon or incorporate into any product or service Customer provides to a
            third party, (iv) use in any manner for the purpose of developing, distributing or
            making accessible products or services that are similar to or compete with, (v) sell,
            sublicense, transfer, assign, lease, rent, distribute, or grant a security interest in
            any rights in, (vi) make available on a service bureau basis, as part of any third
            party’s product offering (regardless of hosting or distribution model) or otherwise
            access or use (or permit a third party to access and use) for the benefit of a third
            party, (vii) allow unauthorized persons to have access to, (viii) transmit unlawful,
            infringing or harmful data, content or code to or from, (ix) copy or replicate, (x)
            interfere with, disrupt, or create an undue burden on (or violate the regulations,
            policies, or procedures of) any servers or networks connected to, (xi) attempt to gain
            unauthorized access to or interfere with any license key mechanism in or otherwise
            circumvent any mechanism intended to limit use of, (xii) alter or remove any trademarks
            or proprietary notices contained in or on, (xiii) engage in framing, mirroring, or
            otherwise simulating the appearance or function of, (xiv) perform or publish any
            performance or benchmark tests or analyses relating to, or (xv) otherwise use except as
            expressly permitted hereunder, in each case of (i) through (xv), in whole or in part,
            the Services (and all technology constituting or used to provide the Services) and all
            related Documentation (collectively, “EVRN Technology”).
          </p>
        </div>
        <div>
          <p class="legal-section__lead">1.6. <strong>Acceptable Use Policy</strong>.</p>
          <p>
            This section, and Customer’s use restrictions and obligations therein, is referred to in
            this Agreement as the “Acceptable Use Policy.” The Customer Data made accessible on the
            Services, including any data, models, content, text, and other materials that are
            collected, uploaded to, or otherwise provided to the Services by or on behalf of
            Customer, is the sole responsibility of Customer. This means that Customer, and not
            Cuebric, is solely responsible for all Customer Data that is accessible through the
            Services, including its accuracy, completeness, and suitability. Customer acknowledges
            that Cuebric has no obligation to pre-screen Customer Data, although Cuebric reserves
            the right in its sole discretion to pre-screen, refuse or remove any Customer Data from
            the Services, including if Cuebric believes it violates this Agreement or is otherwise
            objectionable. Customer further agrees, represents, and warrants that: (i) the Customer
            Data will not contain any content or material that is illegal, or include any content or
            material that violates, infringes, or misappropriates any third party’s intellectual
            property rights, constitutes an invasion of privacy or misappropriation of publicity
            rights, (ii) Customer will not use the Services or transmit Customer Data in a manner
            that is or could be harassing, abusive, tortious, threatening, harmful, harmful to
            minors in any way (including by sexualizing or exploiting minors), invasive of another’s
            privacy or publicity rights, vulgar, defamatory, false, intentionally misleading, trade
            libelous, indecent, pornographic, obscene, patently offensive, promotes racism, bigotry,
            hatred, or physical harm of any kind against any group or individual, (iii) the Customer
            Data will not contain any computer code, programs, or programming devices that are
            designed to disrupt, modify, access, delete, damage, deactivate, disable, harm, or
            otherwise impede in any manner, the operation of the Services or any other associated
            software, firmware, hardware, computer system, or network, (iv) Customer will not send
            through the Services unsolicited or unauthorized advertising, promotional materials,
            junk mail, spam, chain letters, pyramid schemes, or any other form of duplicative or
            unsolicited messages, whether commercial or otherwise, (v) Customer will not use the
            Services to harvest, collect, gather or assemble information or data regarding
            third-party users, including email addresses, without their consent, (vi) Customer will
            not interfere with, disrupt, or create an undue burden on servers or networks connected
            to the Services, or violate the regulations, policies or procedures of such networks,
            (vii) Customer will not attempt to gain unauthorized access to the Services (or to other
            computer systems or networks connected to or used together with the Services), whether
            through password mining or any other means, (viii) Customer will not harass or interfere
            with any third-party user’s use and enjoyment of the Services, (ix) Customer will not
            use software or automated agents or scripts to produce multiple Accounts on the
            Services, or to generate automated searches, requests, or queries to (or to strip,
            scrape, or mine data from) the Services, (x) Customer will not provide or make
            accessible on the Services any Customer Data that is otherwise objectionable to Cuebric
            in its sole discretion; and (xi) Customer will not upload, submit, or otherwise provide
            to the Services any image, photograph, video, or other visual depiction of an
            identifiable natural person without having obtained all necessary rights,
            authorizations, consents, and releases from such person (or, in the case of a minor,
            from such person’s parent or legal guardian), and will not use the Services to generate,
            create, or produce any Output that depicts, simulates, or is intended to represent the
            likeness of an identifiable natural person for purposes of deception, impersonation,
            fraud, or any other unlawful or unauthorized purpose. Cuebric reserves the right (but
            has no obligation) to review, refuse and/or remove any Customer Data in its sole
            discretion, and to investigate and/or take appropriate action against Customer in
            Cuebric’s sole discretion if Customer violates the Acceptable Use Policy or any other
            provision of this Agreement or otherwise creates liability for Cuebric or any other
            person. Such appropriate action may include removing or modifying the Customer Data,
            terminating the Account in accordance with this Agreement, and/or reporting Customer to
            law enforcement authorities.
          </p>
        </div>
      </div>
    </section>

    <section class="legal-section" aria-labelledby="section-2">
      <h2 id="section-2" class="legal-section__title title-small-dark">2. Fees and Payment</h2>
      <div class="legal-section__body">
        <div>
          <p class="legal-section__lead">2.1. <strong>Fees</strong>.</p>
          <p>
            Fees for the Services are assessed on a per-credit basis. Customer may elect to
            subscribe to a recurring subscription to the Services (a “Subscription”) by executing an
            Order for such Services. Each Subscription tier grants Customer access to process up to
            its applicable Monthly Credit Allowance during each billing period. Unused credits in
            any given month do not carry over to subsequent months unless expressly stated in the
            applicable Order.
          </p>
        </div>
        <div>
          <p class="legal-section__lead">2.2. <strong>Welcome Tier</strong>.</p>
          <p>
            Cuebric may make certain features of the Services temporarily available to Customer at a
            flat reduced rate as a welcome offering (the “Welcome Tier”). Customer acknowledges and
            agrees that the Welcome Tier is subject to limitations, which may include restrictions
            on the number of credits available, the number of objects per Scene, storage capacity,
            and available features, in each case as set forth in the Documentation or the applicable
            Order. Cuebric reserves the right to modify, limit, or discontinue the Welcome Tier (or
            any aspect thereof) at any time in its sole discretion and without liability of any
            kind. Unless expressly indicated in the applicable Order: (i) Cuebric will be free to
            terminate or suspend Customer’s access to the Welcome Tier for any reason at any time
            and without liability of any kind, and (ii) any such access to the Welcome Tier is
            provided on an “AS IS” and “AS AVAILABLE” basis without warranty or support of any kind,
            express or implied.
          </p>
        </div>
        <div>
          <p class="legal-section__lead">2.3. <strong>Subscription Tiers</strong>.</p>
          <p>
            Cuebric offers certain Subscription tiers, as further described on Cuebric’s
            “Subscription Plans” page available at
            <a routerLink="/plans">evrn.world/plans</a>
            (“Subscription Plans Page”). For the avoidance of doubt, each Subscription tier entitles
            Customer to process up to its applicable Monthly Credit Allowance during each billing
            period; Scenes processed in excess of the Monthly Credit Allowance may be subject to
            additional per-credit fees as set forth in the applicable Order. Please refer to the
            Subscription Plans Page for additional information.
          </p>
        </div>
        <div>
          <p class="legal-section__lead">2.4. <strong>Payment Terms</strong>.</p>
          <p>
            Customer agrees to pay to Cuebric the fees for each of the Services that Customer
            purchases or uses in accordance with the pricing and payment terms set forth in the
            applicable Order, consistent with the Subscription tier Customer has purchased. Fees and
            payment terms may be updated in accordance with the terms of this Agreement. Unless
            otherwise expressly set forth in the applicable Order, all recurring fees for
            Subscriptions will be billed to Customer in advance for the Monthly Credit Allowance
            applicable to the selected tier. Unless otherwise expressly set forth in the applicable
            Order, Customer’s Subscription will continue and automatically renew on a recurring
            basis at regular intervals in accordance with Customer’s elections at the time of sign
            up or in the applicable Order, unless and until Customer terminates Customer’s
            Subscription in accordance with Section 3.3 (Termination of Subscriptions), or
            Customer’s Account is otherwise suspended or terminated pursuant to this Agreement. The
            applicable fees for any Renewal Subscription Terms (defined in Section 3.2 (Subscription
            Term; Automatic Renewal)) will be charged at Cuebric’s then-current published list price
            as set forth on the Subscription Plans Page, until the Subscription or this Agreement
            terminates in accordance with this Agreement. Cuebric reserves the right to change the
            terms, including pricing and Monthly Credit Allowances, for Subscriptions from time to
            time in accordance with Section 7.4 (Modifications to this Agreement). If Customer does
            not agree with such changes, Customer may terminate Customer’s Subscription in
            accordance with Section 3.3 (Termination of Subscriptions). Cuebric will use
            commercially reasonable efforts to notify Customer of these changes, including by
            sending a notice to an email address associated with Customer’s Account.
          </p>
        </div>
        <div>
          <p class="legal-section__lead">2.5. <strong>Payment Method</strong>.</p>
          <p>
            Customer will be billed for all amounts due under this Agreement using the payment
            method provided by Customer to Cuebric’s third-party payment processor at the time of
            sign-up; provided, however, that Customer may update its payment method by contacting
            Cuebric’s support team at
            <a href="mailto:support@evrn.world" target="_blank" rel="noopener noreferrer"
              >support&#64;evrn.world</a
            >. Customer must provide current, complete, and accurate information for Customer’s
            billing account and promptly update all information to keep Customer’s billing account
            current, complete, and accurate. Fees paid by Customer are non-refundable, except as
            provided in this Agreement or when required by law. In the event of a failed attempt to
            charge Customer’s payment method, Cuebric reserves the right to retry billing Customer’s
            payment method. In the event that Customer or Cuebric (through Cuebric’s third-party
            payment service providers) update Customer’s credit card information to remedy a change
            in validity or expiration date, Cuebric may automatically resume billing Customer for
            its Subscription. Cuebric may suspend or cancel Customer’s access to the Services
            associated with Customer’s Subscription if Cuebric remains unable to successfully charge
            the payment method associated with Customer’s Account. Additionally, Cuebric may charge
            Customer interest on overdue fees at the rate of 1.5% per month (or the highest rate
            permitted by law, if less) on the amount overdue.
          </p>
        </div>
        <div>
          <p class="legal-section__lead">2.6. <strong>Automatic Renewal</strong>.</p>
          <p>
            Customer must terminate any automatically renewing Subscription at least five (5) days
            before it renews in accordance with this Agreement in order to avoid being charged for
            the upcoming billing period. By choosing an automatically renewing Subscription plan,
            Customer acknowledges and accepts this recurring payment feature and Customer accepts
            responsibility for all recurring charges prior to termination. If there are no valid
            payment methods on file for Customer, Cuebric may send Customer invoices for any amounts
            due, and Customer will pay such invoices within thirty (30) days of Customer’s receipt
            thereof.
          </p>
        </div>
        <div>
          <p class="legal-section__lead">2.7. <strong>Taxes</strong>.</p>
          <p>
            Prices do not include, and Customer must pay or reimburse Cuebric for, all federal,
            state, local, sales, use, value added, excise, or other taxes, fees, or duties arising
            out of this Agreement, or the transactions contemplated by this Agreement (other than
            taxes based on Cuebric’s net income). If Cuebric has a legal obligation to pay or
            collect sales tax for which Customer is responsible, Cuebric will calculate the sales
            tax based upon the billing information it has about Customer and charge Customer that
            amount (which, if Customer’s billing information is incomplete or inaccurate, may be the
            highest prevailing rate then in effect), unless Customer timely provides Cuebric with a
            valid tax exemption certificate acceptable to the appropriate taxing authority.
          </p>
        </div>
      </div>
    </section>

    <section class="legal-section" aria-labelledby="section-3">
      <h2 id="section-3" class="legal-section__title title-small-dark">3. Term and Termination</h2>
      <div class="legal-section__body">
        <div>
          <p class="legal-section__lead">3.1. <strong>Term</strong>.</p>
          <p>
            This Agreement will start on the Effective Date and, unless terminated earlier in
            accordance with this Agreement, will remain in full force and effect while Customer
            retains access to or uses any feature of the Services (the “Term”). In the event that
            all Orders have expired or terminated, either party may terminate this Agreement upon
            written notice to the other party.
          </p>
        </div>
        <div>
          <p class="legal-section__lead">
            3.2. <strong>Subscription Term; Automatic Renewal</strong>.
          </p>
          <p>
            The duration of Customer’s Initial Subscription Term for each Order will be as set forth
            in such Order and, unless otherwise indicated in the Order, shall automatically renew on
            a recurring basis for additional subscription periods of the same duration as the
            Initial Subscription Term (as applicable, a “Renewal Subscription Term”) until
            terminated or cancelled by Customer or Cuebric as set forth below. The Initial
            Subscription Term of an Order, together with any applicable Renewal Subscription Term(s)
            for such Order, are collectively, the “Subscription Term.”
          </p>
        </div>
        <div>
          <p class="legal-section__lead">3.3. <strong>Termination of Subscriptions</strong>.</p>
          <p>
            Unless otherwise set forth in an Order, either party may cancel any Subscription(s)
            under this Agreement by providing the other party with at least five (5) days’ notice of
            its intent to terminate prior to the end of the then-current Subscription Term. Customer
            should send notices of termination to
            <a href="mailto:support@evrn.world" target="_blank" rel="noopener noreferrer"
              >support&#64;evrn.world</a
            >. If Customer cancels or Cuebric terminates a Subscription as set forth above, then
            Customer will continue to have access to the applicable features of the Services until
            completion of Customer’s then-current Subscription Term (or, if terminated within five
            (5) days of renewal, completion of the next Renewal Subscription Term), provided that
            Customer has timely paid all applicable fees and remains in compliance with the terms of
            this Agreement.
          </p>
        </div>
        <div>
          <p class="legal-section__lead">3.4. <strong>Termination for Cause</strong>.</p>
          <p>
            Cuebric may terminate this Agreement and/or any applicable Order, in whole or in part,
            by written notice if Customer fails to pay within ten (10) days after notice of
            nonpayment for any amounts owed to Cuebric. Additionally, either party may terminate
            this Agreement and all Orders in the event that: (i) the other party is in material
            breach of this Agreement, which is not cured within thirty (30) days after written
            notice of such breach, or (ii) the other party files for or is adjudicated bankrupt or
            suffers any other analogous event.
          </p>
        </div>
        <div>
          <p class="legal-section__lead">3.5. <strong>Effect of Termination</strong>.</p>
          <p>
            Upon the effective date of expiration or termination of this Agreement for any reason:
            (i) all outstanding Orders and access to EVRN Technology will automatically terminate,
            and (ii) all outstanding payment obligations of Customer become due and payable
            immediately. All definitions and the following provisions will survive the expiration or
            termination of this Agreement for any reason: Sections 1.5 (Restrictions), 2.4 (Payment
            Terms), 2.7 (Taxes), 3.5 (Effect of Termination), 3.6 (Suspension), and 4 (License;
            Ownership) through 7 (General Provisions).
          </p>
        </div>
        <div>
          <p class="legal-section__lead">3.6. <strong>Suspension</strong>.</p>
          <p>
            Cuebric reserves the right to suspend, terminate, or downgrade Customer’s access to or
            use of any or all of the Services, in its sole discretion, if any amount under an Order
            is due but unpaid until such time as all amounts due under this Agreement are paid in
            full. In addition to the amount due for the Services, Customer will be charged with fees
            or charges that are incidental to any chargebacks or collection of any such unpaid
            amounts including collection fees. Additionally, Cuebric reserves the right to suspend
            Customer’s access to the Services or any portion thereof at any time: (i) in the event
            that Cuebric suspects that Customer is using the Services in violation of this
            Agreement, or (ii) if Cuebric otherwise believes such action is reasonable to comply
            with any applicable law, regulation or court order.
          </p>
        </div>
      </div>
    </section>

    <section class="legal-section" aria-labelledby="section-4">
      <h2 id="section-4" class="legal-section__title title-small-dark">4. License; Ownership</h2>
      <div class="legal-section__body">
        <div>
          <p class="legal-section__lead">4.1. <strong>Customer Data</strong>.</p>
          <p>
            Customer will retain ownership of all of its rights in any content, images, materials,
            data, or information that is uploaded, transmitted, or otherwise provided to the
            Services by or on behalf of, or at the direction of, Customer, including any Output
            generated through the Services (collectively, the “Customer Data”). As between Customer
            and Cuebric, all Output is owned by Customer. Cuebric is hereby granted a worldwide,
            non-exclusive, royalty-free, fully paid-up, transferable, sublicensable, irrevocable,
            right and license to use, copy, reproduce, modify, adapt, prepare derivative works from,
            translate, distribute, perform, and display the Customer Data (in whole or in part): (a)
            for the purposes of operating and providing the Services to Customer as described by
            this Agreement; and (b) to create de-identified data from the Customer Data
            (“De-Identified Data”). Without limiting the generality of the foregoing, to the extent
            any Customer Data includes the name, image, or likeness of any individual, Customer
            represents and warrants that Customer has obtained all necessary rights, authorizations,
            consents, releases, and permissions from such individual(s) to include such name, image,
            or likeness in the Customer Data and to grant the rights granted herein with respect
            thereto, including the right for Cuebric and the Services to generate 3D images, Scenes,
            and other Output depicting such individual(s). Cuebric may use, copy, reproduce, modify,
            adapt, prepare derivative works from, translate, distribute, perform, and display
            De-Identified Data to train, fine-tune, and improve Cuebric’s AI and machine learning
            models, provided that any De-Identified Data used to train, fine-tune, or improve such
            models is stripped of identifiers, including facial geometry and other biometric
            identifiers, such that it does not identify, and cannot reasonably be used to identify,
            any individual.
          </p>
        </div>
        <div>
          <p class="legal-section__lead">4.2. <strong>Platform Data</strong>.</p>
          <p>
            Cuebric may collect, access, process, aggregate, and de-identify data derived from
            Customer’s use of the Services and Cuebric’s provision and operation of the Services and
            related systems and technologies, including Scene-level attributes (e.g., Scene type,
            spatial characteristics, object categories, and lighting conditions), log data,
            telemetry, and similar operational and usage information (collectively, “Platform
            Data”). Cuebric may use Platform Data for any business purpose, including to
            troubleshoot, operate, improve, and enhance the Services, and for development,
            diagnostic, security, corrective, statistical modeling, machine learning, product
            analytics, and regression analysis purposes, as well as to sell or license Platform Data
            to third parties. Platform Data will not include any personally identifiable
            information, Customer-specific identifiers, or raw Customer Data, and will be processed
            in a manner that prevents re-identification of individual Customers or their specific
            Scenes. Cuebric’s use and commercialization of Platform Data is not subject to any
            revenue sharing obligation to Customer. Customer hereby grants Cuebric a perpetual,
            worldwide, non-exclusive, royalty-free, fully paid-up, transferable, sublicensable,
            irrevocable right and license to collect, process, aggregate, de-identify, use, and
            commercialize Platform Data as described in this section. For clarity, Platform Data is
            not Customer Data.
          </p>
        </div>
        <div>
          <p class="legal-section__lead">4.3. <strong>Data Privacy and Security</strong>.</p>
          <p>
            Cuebric will process Customer Data only as is reasonably necessary to provide the
            Services and as otherwise set forth in this Agreement. Cuebric will implement and
            maintain commercially reasonable technical and organizational measures designed to
            protect Customer Data against accidental, unauthorized, or unlawful destruction, loss,
            alteration, or disclosure. Cuebric will not disclose, share, or otherwise make Customer
            Data accessible to any third party, except (a) as expressly authorized by Customer, (b)
            to Cuebric’s authorized subprocessors and service providers who are bound by written
            confidentiality and data protection obligations no less protective than those set forth
            herein, or (c) to Third-Party AI Providers solely to the extent necessary to process a
            Scene, subject to those providers’ confidentiality obligations. Customer shall ensure
            (and is solely responsible for ensuring) that it has given such notices to and obtained
            such consents and permissions from all relevant third parties, and has reserved all
            rights, in each case, as may be required under applicable law or otherwise for Cuebric
            to process Customer Data to provide the Services as contemplated by this Agreement.
          </p>
        </div>
        <div>
          <p class="legal-section__lead">4.4. <strong>Customer Data Storage</strong>.</p>
          <p>
            Customer is solely responsible for backing up Customer Data and Cuebric expressly
            disclaims all warranties or obligations with respect to storage or back up of Customer
            Data. Without limiting the foregoing, Cuebric reserves the right to delete any and all
            Customer Data in its discretion. Upon Customer’s request during the Subscription Term or
            for a period of thirty (30) days thereafter (except where Cuebric has terminated the
            Agreement for cause), Cuebric will use commercially reasonable efforts to make Customer
            Data that is compliant with this Agreement (including the Acceptable Use Policy herein)
            available for export in a commonly used, machine-readable format.
          </p>
        </div>
        <div>
          <p class="legal-section__lead">4.5. <strong>Ownership</strong>.</p>
          <p>
            Except for the limited rights granted in this Agreement, Cuebric hereby retains all
            right, title and interest, including all intellectual property rights, in and to the
            EVRN Technology. ALL RIGHTS NOT EXPRESSLY GRANTED HEREUNDER ARE RESERVED BY CUEBRIC.
          </p>
        </div>
        <div>
          <p class="legal-section__lead">4.6. <strong>Feedback</strong>.</p>
          <p>
            Customer hereby grants to Cuebric and its affiliates a worldwide, irrevocable,
            perpetual, sublicensable, royalty-free right and license to use, modify, transmit,
            reproduce, make derivative works of, disclose and exploit without restriction all
            feedback and suggestions provided by Customer (collectively, “Feedback”), including any
            information about operating results, known or suspected bugs, errors or compatibility
            problems, suggested modifications, and user-desired features, regarding the EVRN
            Technology or any portion thereof.
          </p>
        </div>
      </div>
    </section>

    <section class="legal-section" aria-labelledby="section-5">
      <h2 id="section-5" class="legal-section__title title-small-dark">5. Indemnification</h2>
      <div class="legal-section__body">
        <div>
          <p class="legal-section__lead">5.1. <strong>Customer Indemnity</strong>.</p>
          <p>
            Customer shall indemnify, defend, and hold harmless Cuebric and its officers, directors,
            employees, consultants, affiliates, subsidiaries, and agents (collectively, the “Cuebric
            Entities”) from and against any third-party claim, loss, or damage (including reasonable
            attorneys’ fees), arising out of or relating to: (i) any allegation that Cuebric’s use
            of the Customer Data in accordance with this Agreement infringes or misappropriates any
            third party’s intellectual property rights or violates applicable law, (ii) use of the
            Services in combination with material, content, software, technology, products, data or
            services not developed and provided by Cuebric, including Customer Data, (iii)
            Customer’s violation of the terms of this Agreement, including Sections 1.4 (AI
            Functionality) or 1.5 (Restrictions) or the Acceptable Use Policy, or (iv) use of the
            Output. Cuebric will provide Customer with: (a) prompt written notice of; (b) control
            over the defense and settlement of; and (c) all information and assistance reasonably
            requested by Customer (each at Customer’s sole expense) in connection with the defense
            or settlement of, any such claim. Notwithstanding the foregoing, Cuebric will at all
            times have the option to participate in any matter or litigation, including
            participation through counsel of its own selection, if desired, the hiring of such
            separate counsel being at Cuebric’s own expense.
          </p>
        </div>
        <div>
          <p class="legal-section__lead">5.2. <strong>Cuebric Indemnity</strong>.</p>
          <p>
            Cuebric shall indemnify, defend, and hold harmless Customer and its officers, directors,
            employees, and agents from and against any third-party claim alleging that Customer’s
            use of the Services as permitted under this Agreement infringes or misappropriates such
            third party’s United States patent, copyright, trademark, or trade secret rights. The
            foregoing obligation will not apply to the extent any claim arises from: (a) use of the
            Services in combination with any materials, data, software, content, or services not
            provided by Cuebric, including any Customer Data; (b) Customer’s modification of the
            Services or any component thereof; (c) Customer’s use of the Services other than in
            accordance with this Agreement and the Documentation; (d) Customer’s continued use of
            the allegedly infringing Services after Cuebric has provided Customer with a
            non-infringing alternative or has notified Customer of a modification or workaround; or
            (e) use of other than the then-current version of the Services, where the infringement
            would have been avoided by use of the then-current version. If the Services become, or
            in Cuebric’s reasonable opinion are likely to become, the subject of a claim of
            infringement, Cuebric may, at its sole option and expense: (i) procure for Customer the
            right to continue using the Services; (ii) replace or modify the Services so that they
            become non-infringing without material diminution in functionality; or (iii) if neither
            (i) nor (ii) is commercially practicable, terminate the applicable Order and refund to
            Customer any prepaid, unused fees for the remainder of the then-current Subscription
            Term. This section states Cuebric’s sole liability, and Customer’s sole and exclusive
            remedy, with respect to any claim of intellectual property infringement relating to the
            Services.
          </p>
        </div>
      </div>
    </section>

    <section class="legal-section" aria-labelledby="section-6">
      <h2 id="section-6" class="legal-section__title title-small-dark">
        6. Warranties; Disclaimer; Limitation of Liability
      </h2>
      <div class="legal-section__body">
        <div>
          <p class="legal-section__lead">6.1. <strong>Customer Warranties</strong>.</p>
          <p>
            Customer represents, warrants, and covenants that it has and will maintain during the
            Term all necessary right, title, interest, authorizations, and permissions to: (i) grant
            rights to, access, provide, provide access to, or request Cuebric access, disclose, or
            submit, any Customer Data and/or Feedback, and (ii) access and permit Cuebric to access
            on Customer’s behalf any Customer Data, as applicable.
          </p>
        </div>
        <div>
          <p class="legal-section__lead">6.2. <strong>Disclaimer</strong>.</p>
          <p>
            TO THE FULLEST EXTENT PERMITTED BY APPLICABLE LAW AND EXCEPT AS EXPRESSLY SET FORTH IN
            THIS AGREEMENT: (i) THE EVRN TECHNOLOGY AND ANY OTHER MATERIALS AND CONTENT MADE
            AVAILABLE BY CUEBRIC OR THROUGH THE SERVICES ARE PROVIDED “AS IS” AND ON AN “AS
            AVAILABLE” BASIS; (ii) THE CUEBRIC ENTITIES DISCLAIM ALL WARRANTIES OF ANY KIND, WHETHER
            EXPRESS, STATUTORY, OR IMPLIED, RELATING TO THE EVRN TECHNOLOGY AND ANY OTHER MATERIALS
            AND CONTENT MADE AVAILABLE BY CUEBRIC OR THROUGH THE SERVICES, INCLUDING ANY WARRANTY OF
            MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, TITLE, QUIET ENJOYMENT,
            NON-INFRINGEMENT, LOSS OF DATA, ACCURACY OF RESULTS, OR ARISING FROM COURSE OF DEALING,
            USAGE, TRADE, OR RELIANCE. THE CUEBRIC ENTITIES DO NOT WARRANT ANY THIRD-PARTY CONTENT
            OR FUNCTIONALITY. TO THE FULLEST EXTENT PERMITTED BY LAW, THE CUEBRIC ENTITIES DO NOT
            WARRANT THAT THE EVRN TECHNOLOGY AND ANY OTHER MATERIALS, RECOMMENDATIONS OR CONTENT
            MADE AVAILABLE THROUGH THE EVRN TECHNOLOGY (INCLUDING THE SERVICES) WILL BE
            UNINTERRUPTED, SECURE, OR FREE OF ERRORS, VIRUSES, OR OTHER HARMFUL COMPONENTS, AND DO
            NOT WARRANT THAT ANY OF THOSE ISSUES WILL BE CORRECTED.
          </p>
        </div>
        <p>
          NO ADVICE OR INFORMATION, WHETHER ORAL OR WRITTEN, OBTAINED BY CUSTOMER FROM THE EVRN
          TECHNOLOGY (INCLUDING THE SERVICES) WILL CREATE ANY WARRANTY THAT IS NOT EXPRESSLY STATED
          IN THIS AGREEMENT. AS BETWEEN THE PARTIES, CUSTOMER IS SOLELY RESPONSIBLE FOR REVIEWING
          AND DETERMINING WHETHER OR NOT, OR HOW TO, USE ANY OUTPUT, CONTENT, MATERIALS OR DATA THAT
          IS MADE AVAILABLE VIA THE SERVICES. WITHOUT LIMITING THE FOREGOING, AS BETWEEN THE
          PARTIES, CUSTOMER IS SOLELY RESPONSIBLE FOR, AND CUEBRIC WILL HAVE NO LIABILITY FOR, ANY
          DECISIONS MADE BY CUSTOMER BASED UPON ANY OUTPUT, CONTENT, MATERIALS OR DATA THAT IS
          PROVIDED BY THE SERVICES, INCLUDING ANY DECISIONS MADE BY CUSTOMER IN CONNECTION WITH THE
          SERVICES, REGARDLESS OF ANY RESULTS OR OUTPUT GENERATED BY THE SERVICES.
        </p>
        <p>
          TO THE FULLEST EXTENT PERMITTED BY LAW, THE CUEBRIC ENTITIES ARE NOT RESPONSIBLE FOR ANY
          DAMAGE THAT MAY RESULT FROM CUSTOMER’S USE OF OR ACCESS TO THE EVRN TECHNOLOGY OR ANY
          OTHER OUTPUT, MATERIALS, DATA OR CONTENT THAT IS MADE AVAILABLE BY CUEBRIC OR THROUGH THE
          SERVICES. CUSTOMER UNDERSTANDS AND AGREES THAT CUSTOMER’S USE OF THE EVRN TECHNOLOGY
          (INCLUDING THE SERVICES) AND ANY OUTPUT, CONTENT, DATA OR MATERIALS THAT ARE ACCESSED OR
          OTHERWISE OBTAINED FROM CUEBRIC OR THROUGH THE EVRN TECHNOLOGY IS AT CUSTOMER’S OWN
          DISCRETION AND RISK, AND THAT, TO THE FULLEST EXTENT PERMITTED BY LAW, THE CUEBRIC
          ENTITIES ARE NOT RESPONSIBLE FOR ANY DAMAGE TO CUSTOMER’S PROPERTY OR ANY LOSS OF DATA OR
          CUSTOMER DATA.
        </p>
        <div>
          <p class="legal-section__lead">6.3. <strong>Limitation of Liability</strong>.</p>
          <p>
            TO THE FULLEST EXTENT PERMITTED BY APPLICABLE LAW, IN NO EVENT WILL THE CUEBRIC ENTITIES
            BE LIABLE FOR ANY INDIRECT, SPECIAL, INCIDENTAL, EXEMPLARY, PUNITIVE, TREBLE OR
            CONSEQUENTIAL DAMAGES (INCLUDING, WITHOUT LIMITATION, LOSS OF BUSINESS, REVENUE,
            PROFITS, GOODWILL, DATA OR OTHER ECONOMIC ADVANTAGE) ARISING OUT OF OR RELATING TO THIS
            AGREEMENT, HOWEVER CAUSED AND WHETHER BASED ON BREACH OF CONTRACT, BREACH OF WARRANTY,
            TORT (INCLUDING NEGLIGENCE), PRODUCT LIABILITY OR ANY OTHER THEORY OF LIABILITY, EVEN IF
            ADVISED OF THE POSSIBILITY OF SUCH DAMAGES. TO THE FULLEST EXTENT PERMITTED BY
            APPLICABLE LAW, IN NO EVENT WILL THE CUEBRIC ENTITIES’ TOTAL LIABILITY (INCLUDING
            ATTORNEYS’ FEES) ARISING OUT OF OR RELATED TO THIS AGREEMENT EXCEED THE GREATER OF (I)
            AMOUNTS PAID BY CUSTOMER UNDER THIS AGREEMENT DURING THE 12-MONTH PERIOD PRIOR TO THE
            DATE THE CLAIM AROSE; OR (II) ONE HUNDRED DOLLARS ($100). EXCEPT FOR ANY ACTION BY
            CUEBRIC FOR NON-PAYMENT, NEITHER PARTY MAY BRING ANY ACTION, REGARDLESS OF FORM, ARISING
            OUT OF THIS AGREEMENT MORE THAN TWELVE (12) MONTHS AFTER THE DATE THE CLAIM AROSE. EACH
            PROVISION OF THIS AGREEMENT THAT PROVIDES FOR A LIMITATION OF LIABILITY, DISCLAIMER OF
            WARRANTIES, OR EXCLUSION OF DAMAGES IS INTENDED TO AND DOES ALLOCATE THE RISKS BETWEEN
            THE PARTIES UNDER THESE TERMS. THIS ALLOCATION IS AN ESSENTIAL ELEMENT OF THE BASIS OF
            THE BARGAIN BETWEEN THE PARTIES. EACH OF THESE PROVISIONS IS SEVERABLE AND INDEPENDENT
            OF ALL OTHER PROVISIONS OF THESE TERMS. THESE LIMITATIONS SHALL APPLY NOTWITHSTANDING
            ANY FAILURE OF ESSENTIAL PURPOSE OF ANY LIMITED OR EXCLUSIVE REMEDY.
          </p>
        </div>
      </div>
    </section>

    <section class="legal-section" aria-labelledby="section-7">
      <h2 id="section-7" class="legal-section__title title-small-dark">7. General Provisions</h2>
      <div class="legal-section__body">
        <div>
          <p class="legal-section__lead">7.1. <strong>Assignment</strong>.</p>
          <p>
            Except as expressly set forth in this Agreement, neither party may assign this
            Agreement, or any of its rights or obligations under this Agreement, without the prior
            written consent of the other party, except that Cuebric may assign this Agreement
            without the written consent of Customer as part of the conversion to a corporation or
            other corporate reorganization, upon a change of control, consolidation, merger,
            reincorporation, sale of all or substantially all of its assets related to this
            Agreement or a similar transaction or series of transactions. Subject to the foregoing,
            this Agreement will be binding upon and inure to the benefit of the parties and their
            respective permitted successors and assigns.
          </p>
        </div>
        <div>
          <p class="legal-section__lead">7.2. <strong>Force Majeure</strong>.</p>
          <p>
            Except for the obligation to pay money, neither party will be liable for any failure or
            delay in its performance under this Agreement due to any cause beyond its reasonable
            control, including an act of war, terrorism, act of God, earthquake, flood, embargo,
            riot, sabotage, labor shortage or dispute, governmental act or failure of the Internet.
            The delayed party shall give the other party notice of such cause and shall use its
            commercially reasonable efforts to correct such failure or delay in performance.
          </p>
        </div>
        <div>
          <p class="legal-section__lead">7.3. <strong>Governing Law and Venue</strong>.</p>
          <p>
            This Agreement and any action related thereto will be governed and interpreted by and
            under the laws of the State of New York, without giving effect to any conflicts of laws
            principles that require the application of the law of a different jurisdiction. Customer
            hereby expressly consents to the personal jurisdiction and venue in the state and
            federal courts for New York City, New York for any lawsuit filed there against Customer
            by Cuebric arising from or related to this Agreement. The United Nations Convention on
            Contracts for the International Sale of Goods does not apply to this Agreement.
          </p>
        </div>
        <div>
          <p class="legal-section__lead">7.4. <strong>Modifications to this Agreement</strong>.</p>
          <p>
            Cuebric may modify this Agreement from time to time by giving notice to Customer through
            Cuebric’s online user interfaces, by sending Customer an email to an email address
            associated with Customer’s Account, by prominently posting notice of the changes on the
            Services, or in any other manner permitted by this Agreement. In the event that the last
            email address that Customer has provided is not valid, or for any reason is not capable
            of delivering to Customer the notice described above, Cuebric’s dispatch of the email
            containing such notice will nonetheless constitute effective notice of the changes to
            this Agreement described in the notice. Unless a shorter period is specified by Cuebric
            (e.g., due to changes in the law or exigent circumstances), the modifications become
            effective upon the earlier of (i) thirty (30) days after Cuebric’s notification or (ii)
            renewal of Customer’s current Subscription Term or entry into a new Order. If Cuebric
            specifies that the modifications to this Agreement will take effect prior to Customer’s
            next renewal or Order and Customer notifies Cuebric in writing at
            <a href="mailto:support@evrn.world" target="_blank" rel="noopener noreferrer"
              >support&#64;evrn.world</a
            >
            of Customer’s objection to the modifications within thirty (30) days after the date of
            such notice, Cuebric (at its option and as Customer’s exclusive remedy) will either: (a)
            permit Customer to continue under the existing version of the Agreement until expiration
            of the then-current Subscription Term, if any (after which time the modified Agreement
            will go into effect), or (b) allow Customer to terminate this Agreement. Customer may be
            required to click to accept or otherwise agree to the modified Agreement in order to
            continue using the Services, and, in any event, continued use of the Services after the
            modified version of this Agreement becomes effective will constitute Customer’s
            acceptance of such modified version.
          </p>
        </div>
        <div>
          <p class="legal-section__lead">7.5. <strong>Export</strong>.</p>
          <p>
            Customer agrees that Customer will not, directly or indirectly, export or re-export, or
            knowingly permit the export or re-export of, the EVRN Technology or any technical
            information about the EVRN Technology to any country for which such export or re-export
            is restricted by any applicable U.S. regulation or statute, without the prior written
            consent, if required, of the Bureau of Export Administration of the U.S. Department of
            Commerce, or such other government entity as may have jurisdiction over such export or
            re-export. Customer hereby represents and warrants that: (i) Customer is not located in
            a country that is subject to a U.S. Government embargo, or that has been designated by
            the U.S. Government as a “terrorist supporting” country, and (ii) Customer is not listed
            on any U.S. Government list of prohibited or restricted parties.
          </p>
        </div>
        <div>
          <p class="legal-section__lead">7.6. <strong>Miscellaneous</strong>.</p>
          <p>
            This Agreement (together with all Orders) is the sole agreement of the parties
            concerning the subject matter hereof, and it supersedes all prior agreements and
            understandings with respect to such subject matter. In the event of any conflict between
            the terms of an Order and the terms of this Agreement, the terms of this Agreement will
            apply unless the Order expressly indicates that a provision of the Order should
            supersede contrary language in the Agreement. No terms of any purchase order,
            acknowledgement or other form provided by Customer will modify this Agreement,
            regardless of any failure of Cuebric to object to such terms. Any ambiguity in this
            Agreement shall be interpreted equitably without regard to which party drafted this
            Agreement. Except as set forth in Section 7.4 (Modifications to this Agreement), this
            Agreement may only be amended by a writing signed by both parties. This Agreement may be
            executed in counterparts. The headings in this Agreement are inserted for convenience
            and are not intended to affect the interpretation of this Agreement. Any required notice
            shall be given in writing by customary means with receipt confirmed. Notices to Customer
            shall be sent to the address set forth on the Order. Notices to Cuebric shall be given
            to
            <a href="mailto:support@evrn.world" target="_blank" rel="noopener noreferrer"
              >support&#64;evrn.world</a
            >. Notices will be deemed to have been given at the time of actual delivery in person,
            one (1) day after delivery to an overnight courier service, or three (3) days after
            deposit in the mail. The relationship between the parties shall be that of independent
            contractors. Cuebric may use subcontractors. Waiver of any term of this Agreement or
            forbearance to enforce any term by either party shall not constitute a waiver as to any
            subsequent breach or failure of the same term or a waiver of any other term of this
            Agreement. Any provision found to be unlawful, unenforceable or void shall be severed
            from the remainder of this Agreement, and the Agreement will continue in full force and
            effect without such provision.
          </p>
        </div>
      </div>
    </section>
  </div>
</section>
